import * as React from "react";
import { Route, Switch } from "react-router";
import { Link } from "react-router-dom";
import { Query, Mutation, QueryResult, MutationFunc } from "react-apollo";
import gql from "graphql-tag";
import Home from "../views/Home";
import EditHomePage from "../views/EditHomePage";
import NewUser from "../views/NewUser";
import Login from "../views/Login";
import EditUser from "../views/EditUser";
import CreateEditPost from "../views/CreateEditPost";
import PrivateRoute from "../components/PrivateRoute";
import ViewPosts from "../views/ViewPosts";
import ProductIngestions from "../views/ProductIngestions";
import NewUserSuccess from "../views/NewUserSuccess";
import ViewProducts from "../views/ViewProducts";
import "./app.scss";
import EditProduct from "../views/EditProduct";
import NewProduct from "../views/NewProduct";
import AssignFavorites from "../views/AssignFavoritesPage";
import AdminModelAccess from "../views/AdminModelAccess";
import SearchModels from "../views/SearchModels";
import SearchObservatoryBundles from "../views/SearchObservatoryBundles";
import { NewModel } from "../views/Model/NewModel";
import { EditModel } from "../views/Model/EditModel";
import {
  CreateInteractiveReport,
  EditInteractiveReport,
} from "../views/InteractiveReport";
import SearchInteractiveReports from "../views/SearchInteractiveReports";
import {
  NewObservatoryBundle,
  EditObservatoryBundle,
} from "../views/ObservatoryBundle";
import ObservatoryBundleAccess from "../views/ObservatoryBundleAccess";

const hideNavBar = () => {
  const navBar = document.getElementById("navbarNav");
  navBar?.classList.remove("show");
};

const toggleNavBar = () => {
  const navBar = document.getElementById("navbarNav");
  navBar?.classList.toggle("show");
};

const renderSideBar = (data: any, logout: MutationFunc) => {
  let isAdmin = false;

  if (data.viewer.specialPermissions) {
    isAdmin = data.viewer.specialPermissions.isAdmin;
  }

  if (data.viewer.isUserAuthenticated && isAdmin) {
    const canViewIngestions =
      data.viewer &&
      data.viewer.roles &&
      data.viewer.roles.some(
        (role: string) =>
          role.toLowerCase() === "etr master admin" ||
          role.toLowerCase() === "etr research admin"
      );

    const canAssignFavorites = data?.viewer?.roles.some(
      (role: string) => role.toLowerCase() === "etr sales admin"
    );

    return (
      <nav className="navbar navbar-expand-lg navbar-light bg-light">
        <Link
          data-cy="admin-home-link"
          className="navbar-brand"
          to="/"
          onClick={hideNavBar}
        >
          ETR+ Admin
        </Link>
        <button
          onClick={toggleNavBar}
          className="navbar-toggler"
          type="button"
          data-toggle="collapse"
          data-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="navbar-collapse collapse" id="navbarNav">
          <ul className="navbar-nav">
            <li className="nav-item">
              <Link
                data-cy="create-user-link"
                onClick={hideNavBar}
                className="nav-link"
                to="/new-user"
              >
                Create User
              </Link>
            </li>
            <li className="nav-item">
              <Link
                onClick={hideNavBar}
                className="nav-link"
                to="/edit-dashboard"
              >
                Edit Dashboard
              </Link>
            </li>
            <li className="nav-item">
              <Link
                onClick={hideNavBar}
                className="nav-link"
                to="/articles/new"
              >
                Create Article
              </Link>
            </li>
            <li className="nav-item">
              <Link
                onClick={hideNavBar}
                className="nav-link"
                to="/newsletters/new"
              >
                Create Newsletter
              </Link>
            </li>
            <li className="nav-item">
              <Link onClick={hideNavBar} className="nav-link" to="/articles">
                View Posts
              </Link>
            </li>
            <li className="nav-item">
              <Link onClick={hideNavBar} className="nav-link" to="/products">
                Insights
              </Link>
            </li>
            {canViewIngestions && (
              <li className="nav-item">
                <Link
                  onClick={hideNavBar}
                  className="nav-link"
                  to="/product-ingestion"
                >
                  Product Ingestion
                </Link>
              </li>
            )}

            {canAssignFavorites && (
              <li className="nav-item">
                <Link
                  onClick={hideNavBar}
                  className="nav-link"
                  to="/assign-favorites"
                >
                  Assign Favorites
                </Link>
              </li>
            )}

            <li className="nav-item">
              <Link onClick={hideNavBar} className="nav-link" to="/models">
                Models
              </Link>
            </li>
            <li className="nav-item">
              <Link
                onClick={hideNavBar}
                className="nav-link"
                to="/interactive-reports"
              >
                Interactive Reports
              </Link>
            </li>
            <li className="nav-item">
              <Link
                onClick={hideNavBar}
                className="nav-link"
                to="/observatory-bundles"
              >
                Observatory Bundles
              </Link>
            </li>
            <li className="nav-item">
              <a
                data-cy="admin-logout"
                className="nav-link"
                href="#"
                onClick={() =>
                  logout().then(() => window.location.replace("/admin"))
                }
              >
                Logout
              </a>
            </li>
          </ul>
        </div>
      </nav>
    );
  } else {
    return null;
  }
};

const App = (): JSX.Element => (
  <Mutation mutation={LOGOUT}>
    {(logout: MutationFunc) => (
      <Query query={GET_VIEWER}>
        {(result: QueryResult) => {
          const { loading, error, data } = result;
          if (loading) return "Loading...";
          if (error) return "An error occurred!";

          return (
            <div className="wrapper">
              {renderSideBar(data, logout)}
              <div id="content" style={{ height: "100%", overflow: "auto" }}>
                <Switch>
                  <PrivateRoute exact path="/" component={Home} />
                  <PrivateRoute exact path="/new-user" component={NewUser} />
                  <PrivateRoute
                    exact
                    path="/edit-dashboard"
                    component={EditHomePage}
                  />
                  <PrivateRoute
                    exact
                    path="/edit-user/:id"
                    component={EditUser}
                  />
                  <PrivateRoute
                    exact
                    path="/:type(articles|newsletters)/new"
                    component={CreateEditPost}
                  />
                  <PrivateRoute
                    exact
                    path="/:type(articles|newsletters)/:id/edit"
                    component={CreateEditPost}
                  />
                  <PrivateRoute exact path="/articles" component={ViewPosts} />
                  <PrivateRoute
                    exact
                    path="/product-ingestion"
                    component={ProductIngestions}
                  />
                  <PrivateRoute
                    exact
                    path="/new-user/success"
                    component={NewUserSuccess}
                  />
                  <PrivateRoute
                    exact
                    path="/products"
                    component={ViewProducts}
                  />
                  <PrivateRoute
                    exact
                    path="/products/:id/edit"
                    component={EditProduct}
                  />
                  <PrivateRoute
                    exact
                    path="/products/new"
                    component={NewProduct}
                  />
                  <PrivateRoute
                    exact
                    path="/assign-favorites"
                    component={AssignFavorites}
                  />

                  <PrivateRoute exact path="/models/new" component={NewModel} />

                  <PrivateRoute
                    exact
                    path="/models/:id/edit"
                    component={EditModel}
                  />

                  <PrivateRoute
                    exact
                    path="/model/:id/access"
                    component={AdminModelAccess}
                  />

                  <PrivateRoute exact path="/models" component={SearchModels} />

                  <PrivateRoute
                    exact
                    path="/observatory-bundles"
                    component={SearchObservatoryBundles}
                  />

                  <PrivateRoute
                    exact
                    path="/observatory-bundles/new"
                    component={NewObservatoryBundle}
                  />

                  <PrivateRoute
                    exact
                    path="/observatory-bundles/:id/edit"
                    component={EditObservatoryBundle}
                  />

                  <PrivateRoute
                    exact
                    path="/observatory-bundles/:id/access"
                    component={ObservatoryBundleAccess}
                  />

                  <PrivateRoute
                    exact
                    path="/interactive-reports"
                    component={SearchInteractiveReports}
                  />

                  <PrivateRoute
                    exact
                    path="/interactive-reports/:id/edit"
                    component={EditInteractiveReport}
                  />

                  <PrivateRoute
                    exact
                    path="/interactive-reports/new"
                    component={CreateInteractiveReport}
                  />

                  <Route path="/login" component={Login} />
                </Switch>
              </div>
            </div>
          );
        }}
      </Query>
    )}
  </Mutation>
);

const GET_VIEWER = gql`
  {
    viewer {
      id
      isUserAuthenticated
      roles
      specialPermissions {
        isAdmin
      }
    }
  }
`;

const LOGOUT = gql`
  mutation LOGOUT {
    logout {
      id
      isUserAuthenticated
      specialPermissions {
        isAdmin
      }
    }
  }
`;

export default App;
